import type { FC } from 'react';
import styled from 'styled-components';
import type { Link as RequestLink } from 'src/apis/api-utilities';
import type {
  PermissionGroup,
  PermissionGroupDetailsResponse,
} from 'src/apis/authorization-service/types';
import { Button } from 'src/components/buttons-and-actions/button';
import { SplitActions } from 'src/components/buttons-and-actions/split-actions';
import { Stack } from 'src/components/layout/stack';
import { Link } from 'src/components/navigation/link';
import { useModal2 } from 'src/hooks/use-modal';
import type { SavedReferrer } from 'src/hooks/use-referrer';
import { DeletePermissionGroupModal } from 'src/pages/settings/permission-groups/overview/delete-permission-group-modal';
import { UnassignAllUsersPermissionGroupModal } from 'src/pages/settings/permission-groups/overview/unassing-all-users-permission-group-modal';
import { Colors } from 'src/styles';

type Props = {
  permissionGroup: PermissionGroup | PermissionGroupDetailsResponse;
  savedReferrer: SavedReferrer;
  mode: 'overview' | 'details';
  onModalSuccess: () => void;
  createLink?: RequestLink;
  isAdvancedModeEnabled: boolean;
};

export const PermissionGroupActions: FC<Props> = ({
  permissionGroup,
  savedReferrer,
  mode,
  onModalSuccess,
  createLink,
  isAdvancedModeEnabled,
}) => {
  const deleteUrl = permissionGroup._links.delete?.href;
  const unassignAllUsersUrl = permissionGroup._links.unassignAllUsers?.href;

  const canCopyGroup =
    (permissionGroup.type === 'CUSTOM' ||
      permissionGroup.type === 'PLATFORM_MANAGED') &&
    createLink &&
    isAdvancedModeEnabled;

  const deletePermissionGroup = useModal2(
    deleteUrl
      ? {
          modal: (props) => (
            <DeletePermissionGroupModal
              deleteUrl={deleteUrl}
              permissionGroup={permissionGroup}
              onSuccess={onModalSuccess}
              savedReferrer={savedReferrer}
              {...props}
            />
          ),
          trigger: (props) => (
            <Button
              data-testid={`delete-permission-group-action-${permissionGroup.id}`}
              {...props}
            >
              Delete
            </Button>
          ),
        }
      : null
  );

  const unassignAllUsers = useModal2(
    unassignAllUsersUrl
      ? {
          modal: (props) => (
            <UnassignAllUsersPermissionGroupModal
              unassignAllUsersUrl={unassignAllUsersUrl}
              permissionGroup={permissionGroup}
              onSuccess={onModalSuccess}
              savedReferrer={savedReferrer}
              {...props}
            />
          ),
          trigger: (props) => (
            <Button
              data-testid={`unassing-all-users-permission-group-action-${permissionGroup.id}`}
              {...props}
            >
              Unassing all users
            </Button>
          ),
        }
      : null
  );

  return (
    <>
      {deletePermissionGroup?.modal}
      {unassignAllUsers?.modal}
      <Stack flow="column">
        <SplitActions
          testId={`permission-group-${permissionGroup.id}`}
          {...(mode === 'overview'
            ? {
                mode: 'button-secondary',
                size: 'small',
              }
            : {})}
        >
          {mode === 'overview' && (
            <Link
              data-testid={`details-permission-group-action-${permissionGroup.id}`}
              {...savedReferrer.getLinkProps(
                `/settings/permission-groups/${permissionGroup.id}`
              )}
            >
              Details
            </Link>
          )}

          {!permissionGroup.isObsolete && permissionGroup._links.update && (
            <Link
              data-testid={`edit-permission-group-action-${permissionGroup.id}`}
              {...savedReferrer.getLinkProps(
                `/settings/permission-groups/${permissionGroup.id}/edit`
              )}
            >
              Edit
            </Link>
          )}
          {!permissionGroup.isObsolete && canCopyGroup && (
            <Link
              data-testid={`details-permission-group-action-${permissionGroup.id}`}
              {...savedReferrer.getLinkProps(
                `/settings/permission-groups/${permissionGroup.id}/copy`
              )}
            >
              Copy
            </Link>
          )}

          {deletePermissionGroup?.trigger}
          {unassignAllUsers?.trigger}
        </SplitActions>
      </Stack>
    </>
  );
};

const VerticalDivider = styled.div`
  border-left: 0.2rem solid ${Colors.brandLight3};
  height: 80%;
  width: 0.3rem;
  margin: 1.5rem;
`;
